// Live-Retell endpoints — voice tuning + conversation prompts. Post-
// Phase-4 each project has its own bound Retell agent(s), so these
// hooks call /api/workspaces/<wsId>/projects/<pId>/settings/retell-voice
// + .../agent-prompts via the active-scope helper rather than the
// rewrite path.
//
// User preferences (theme, font-size) → useUserPreferences.
// Project operational settings → useProjectSettings.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getActiveApiScope } from "@/lib/api";

function projectScopedPath(suffix: string): string | null {
  const scope = getActiveApiScope();
  if (!scope) return null;
  return `/api/workspaces/${scope.workspaceId}/projects/${scope.projectId}/settings${suffix}`;
}

export interface RetellVoiceConfig {
  voiceSpeed: number;
  interruptionSensitivity: number;
}

export function useRetellVoice() {
  return useQuery<RetellVoiceConfig>({
    queryKey: ["retell-voice"],
    queryFn: () => {
      const path = projectScopedPath("/retell-voice");
      if (!path) throw new Error("No active project");
      return api<RetellVoiceConfig>(path);
    },
    staleTime: 5 * 60_000,
  });
}

export function useUpdateRetellVoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<RetellVoiceConfig>) => {
      const path = projectScopedPath("/retell-voice");
      if (!path) throw new Error("No active project");
      return api<RetellVoiceConfig>(path, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
    },
    onSuccess: (data) => qc.setQueryData(["retell-voice"], data),
  });
}

export interface AgentPrompts {
  outbound: string;
  inbound: string;
}

export function useAgentPrompts() {
  return useQuery<AgentPrompts>({
    queryKey: ["agent-prompts"],
    queryFn: () => {
      const path = projectScopedPath("/agent-prompts");
      if (!path) throw new Error("No active project");
      return api<AgentPrompts>(path);
    },
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAgentPrompts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<AgentPrompts>) => {
      const path = projectScopedPath("/agent-prompts");
      if (!path) throw new Error("No active project");
      return api<{ ok: true }>(path, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
    },
    onSuccess: (_d, vars) => {
      // Optimistically reflect what we sent so the textarea doesn't
      // briefly snap back to the old value before the next refetch.
      qc.setQueryData<AgentPrompts | undefined>(["agent-prompts"], (prev) =>
        prev ? { ...prev, ...vars } : prev,
      );
    },
  });
}
